import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

import { getOrders } from '../../../api';
import { Order, OrderDealStatus } from '../../../types';

const PAGE_SIZE = 25;

// Check if there are more pages based on API response and actual results count
const hasMorePages = (next: number | null, resultsCount: number): boolean => {
  return next !== null && resultsCount === PAGE_SIZE;
};

interface UseOrdersParams {
  statuses: OrderDealStatus[];
  baseParams: {
    product?: string;
    ordering: string;
    staff_id?: number;
    my_orders?: boolean;
  };
  /** When false, prevents initial fetch until enabled. Default: true */
  enabled?: boolean;
}

interface UseOrdersResult {
  orders: Order[];
  totalCount: number;
  statusCounts: Record<string, number>;
  hasMore: boolean;
  isLoading: boolean;
  isInitialLoading: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

type StatusPageState = {
  currentPage: number;
  hasMore: boolean;
  totalCount: number;
};

/**
 * Hook for managing paginated orders across all status columns.
 * Scrolling the main container loads more orders for ALL columns at once.
 */
export const useOrders = ({
  statuses,
  baseParams,
  enabled = true,
}: UseOrdersParams): UseOrdersResult => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [statusPages, setStatusPages] = useState<Record<string, StatusPageState>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Ref to prevent loadMore from firing immediately after refetch
  const refetchTimestampRef = useRef<number>(0);

  // Check if any status has more pages to load
  // Only return true if we have loaded data for all statuses and at least one has more
  const hasMore = useMemo(() => {
    const hasAllStatuses = statuses.every((status) => statusPages[status] !== undefined);
    if (!hasAllStatuses) return false;
    return statuses.some((status) => statusPages[status]?.hasMore === true);
  }, [statuses, statusPages]);

  // Calculate total count across all statuses
  const totalCount = useMemo(() => {
    return statuses.reduce((sum, status) => sum + (statusPages[status]?.totalCount || 0), 0);
  }, [statuses, statusPages]);

  // Get counts per status for column headers
  const statusCounts = useMemo(() => {
    return statuses.reduce(
      (acc, status) => {
        acc[status] = statusPages[status]?.totalCount || 0;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [statuses, statusPages]);

  // Fetch next page for all statuses that have more data
  const fetchNextPageForAllStatuses = useCallback(
    async(isInitial: boolean) => {
      const promises = statuses.map(async(status) => {
        const currentState = statusPages[status];
        const nextPage = isInitial ? 1 : (currentState?.currentPage || 0) + 1;

        // Skip if this status has no more pages (unless initial load)
        if (!isInitial && currentState?.hasMore === false) {
          return { status, results: [], nextPage: currentState.currentPage, hasMore: false, count: currentState.totalCount };
        }

        try {
          const response = await getOrders({
            ...baseParams,
            deal_status: status,
            page: nextPage,
            page_size: PAGE_SIZE,
          });

          return {
            status,
            results: response.results,
            nextPage,
            hasMore: hasMorePages(response.next, response.results.length),
            count: response.count,
          };
        } catch {
          // On error, preserve existing count and mark as no more pages
          const existingCount = statusPages[status]?.totalCount || 0;
          return { status, results: [], nextPage, hasMore: false, count: existingCount };
        }
      });

      return Promise.all(promises);
    },
    [statuses, statusPages, baseParams],
  );

  // Load more orders for all columns
  const loadMore = useCallback(async() => {
    // Debounce: don't load more within 200ms of a refetch completing
    const timeSinceRefetch = Date.now() - refetchTimestampRef.current;
    if (!hasMore || isLoading || timeSinceRefetch < 200) {
      return;
    }

    setIsLoading(true);

    try {
      const results = await fetchNextPageForAllStatuses(false);

      // Accumulate new orders
      const newOrders: Order[] = [];
      const newStatusPages: Record<string, StatusPageState> = { ...statusPages };

      results.forEach(({ status, results: pageResults, nextPage, hasMore: statusHasMore, count }) => {
        newOrders.push(...pageResults);
        newStatusPages[status] = {
          currentPage: nextPage,
          hasMore: statusHasMore,
          totalCount: count,
        };
      });

      setOrders((prev) => [...prev, ...newOrders]);
      setStatusPages(newStatusPages);
    } finally {
      setIsLoading(false);
    }
  }, [hasMore, isLoading, fetchNextPageForAllStatuses, statusPages]);

  // Refetch from page 1 for all statuses
  const refetch = useCallback(async() => {
    setIsInitialLoading(true);
    setIsLoading(true);

    try {
      // Reset status pages for initial fetch
      setStatusPages({});

      const results = await Promise.all(
        statuses.map(async(status) => {
          try {
            const response = await getOrders({
              ...baseParams,
              deal_status: status,
              page: 1,
              page_size: PAGE_SIZE,
            });

            return {
              status,
              results: response.results,
              hasMore: hasMorePages(response.next, response.results.length),
              count: response.count,
            };
          } catch {
            return { status, results: [], hasMore: false, count: 0 };
          }
        }),
      );

      const allOrders: Order[] = [];
      const newStatusPages: Record<string, StatusPageState> = {};

      results.forEach(({ status, results: pageResults, hasMore: statusHasMore, count }) => {
        allOrders.push(...pageResults);
        newStatusPages[status] = {
          currentPage: 1,
          hasMore: statusHasMore,
          totalCount: count,
        };
      });

      setOrders(allOrders);
      setStatusPages(newStatusPages);
    } finally {
      refetchTimestampRef.current = Date.now();
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [statuses, baseParams]);

  // Initial load when statuses or params change
  useEffect(() => {
    if (!enabled) {
      return;
    }
    refetch();
  }, [refetch, enabled]);

  return {
    orders,
    totalCount,
    statusCounts,
    hasMore,
    isLoading,
    isInitialLoading,
    loadMore,
    refetch,
  };
};
